import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CustomerDemoData } from '../../../data';
import { useInit } from '../../Contexts/InitProvider';
import LibModal from '../../features/LibModal/LibModal';
import { useTemplate } from '../../features/template/TemplateProvider';
import useApi from '../../hook/useApi';
import CustomerStatusChangeModal from './Modals/CustomerStatusChangeModal';
import EditCustomerModal from './Modals/EditCustomerModal';

/**
 * @typedef {Object} CustomerContextType
 * @property {number} reloadKey
 * @property {(key:number)=>void} setReloadKey
 * @property {number} infoReloadKey
 * @property {(key:number)=>void} setInfoReloadKey
 * @property {Object} params
 * @property {(params:Object)=>void} setParams
 * @property {Object} customerListData
 * @property {(data:Object)=>void} setCustomerListData
 * @property {boolean} createCustomerModal
 * @property {(show:boolean)=>void} setCreateCustomerModal
 * @property {Object} editCustomerModal
 * @property {(data:Object)=>void} setEditCustomerModal
 * @property {()=>void} handleReload
 * @property {()=>void} handleShowCreateModal
 * @property {(ev:Event, data:Object)=>void} handleShowEditModal
 * @property {(ev:Event, data:Object)=>void} handleCustomerStatusModal
 */

/** @type {import('react').Context<CustomerContextType|null>} */
const CustomerContext = createContext(null);

/**
 * @returns {CustomerContextType}
 */
export const useCustomer = () => useContext(CustomerContext);

function CustomerProvider({ children }) {
    const { setMessage, setConfirm } = useTemplate();
    const [reloadKey, setReloadKey] = useState(0);
    const [infoReloadKey, setInfoReloadKey] = useState(0);
    const [params, setParams] = useState({});
    const { initInfo } = useInit();
    const { get } = useApi();

    const [customerListData, setCustomerListData] = useState({
        customers: CustomerDemoData,
        pagination: {}
    });
    const [createCustomerModal, setCreateCustomerModal] = useState(false);
    const [editCustomerModal, setEditCustomerModal] = useState({
        show: false,
        data: {}
    });

    // -- Load all customer initially
    useEffect(() => {
        setCustomerListData({ customers: CustomerDemoData, pagination: {} });
        if (Object.keys(params).length > 0) {
            get('/api/customers/', {
                q: params.q,
                status: params.status,
                type: params['customer-type'],
                orderby: params['order-by'],
                sort: params.sort,
                page: params.page,
                cpp: 12
            })
                .then(({ data }) => {
                    setCustomerListData(data);
                })
                .catch((err) => {
                    setCustomerListData({ customers: [], pagination: {} });
                    setMessage({ type: 'error', text: err.toString(0) });
                });
        }
    }, [params, reloadKey]);

    const valueCtx = useMemo(() => {
        // --Handle Reload
        const handleReload = () => {
            setReloadKey((d) => d + 1);
        };

        // Create new customer modal show/hide handler
        const handleShowCreateModal = () => setCreateCustomerModal(true);

        // -- Edit Customer Modal
        const handleShowEditModal = (ev, data) => {
            ev?.stopPropagation();
            setEditCustomerModal({ show: true, data });
        };

        // -- Change Customer Status Handler
        const handleCustomerStatusModal = (e, data) => {
            e.stopPropagation();
            setConfirm({
                showButton: false,
                text: (
                    <CustomerStatusChangeModal
                        data={data}
                        setReloadKey={setReloadKey}
                        setInfoReloadKey={setInfoReloadKey}
                    />
                )
            });
        };

        return {
            // -- Reload Key
            reloadKey,
            setReloadKey,
            infoReloadKey,
            setInfoReloadKey,
            // -- Params
            params,
            setParams,
            // -- customer list
            customerListData,
            setCustomerListData,
            // -- create modal
            createCustomerModal,
            setCreateCustomerModal,
            // -- Edit Customer Modal
            editCustomerModal,
            setEditCustomerModal,
            // All Handler
            handleReload,
            // -- Create Customer Modal
            handleShowCreateModal,
            // -- Edit Customer Modal
            handleShowEditModal,
            // -- Customer Status Change Modal
            handleCustomerStatusModal
        };
    }, [
        createCustomerModal,
        customerListData,
        editCustomerModal,
        infoReloadKey,
        initInfo.roles,
        params,
        reloadKey,
        setConfirm
    ]);

    return (
        <CustomerContext.Provider value={valueCtx}>
            {children}
            {editCustomerModal.show && (
                <LibModal
                    title="Edit Customer"
                    setOpen={() => setEditCustomerModal({ show: false, data: {} })}
                    className="md:!w-[550px]"
                >
                    <EditCustomerModal
                        setReloadKey={setReloadKey}
                        setInfoReloadKey={setInfoReloadKey}
                        closeEditModal={() => setEditCustomerModal({ show: false, data: {} })}
                        data={editCustomerModal.data}
                    />
                </LibModal>
            )}
        </CustomerContext.Provider>
    );
}

export default CustomerProvider;
